import type { LucideIcon } from 'lucide-react'
import { BackButton } from 'primitives/back-button'
import { Typography } from 'primitives/typography'
import { Link, useLocation } from 'router-v6'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../primitives/sidebar'

export type ReportItem = { title: string; url: string; icon: LucideIcon }
export type ReportSection = { label: string; items: ReportItem[] }

// A shared constant, not an inline `[]`, so the default keeps one identity across renders.
const NO_SECTIONS: ReportSection[] = []

// Renders the Analytics sections it is given; which sections exist is the app's decision.
export const AppSidebarReports: React.FC<{ sections?: ReportSection[] }> = ({
  sections = NO_SECTIONS,
}) => {
  const location = useLocation()

  return (
    <>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <BackButton data-testid="reports-back-btn" />
          <Typography variant="P/600">Analytics</Typography>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {sections.length === 0 && (
          <div className="p-4 text-s-500 text-muted-foreground">No reports available.</div>
        )}
        {sections.map((section) => (
          <SidebarGroup key={section.label}>
            {/* Default size/color (text-xxs-600 / tertiary) + uppercase to match the main app
                sidebar's section headers — kept consistent across the app. */}
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.url)
                  return (
                    <SidebarMenuItem key={item.title}>
                      {/* No `tooltip` prop: this panel always shows text labels, so the
                          primitive's collapsed-state tooltip would just repeat the visible label. */}
                      <SidebarMenuButton isActive={isActive} asChild>
                        <Link
                          to={item.url}
                          data-testid={`link-report-nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-4 h-4" strokeWidth={isActive ? 2 : 1.5} />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </>
  )
}
